// Typed API client for the data backend. One module so component code
// never assembles URLs or JSON envelopes by hand.

export interface ConfigField {
  name: string;
  label: string;
  type: 'string' | 'int' | 'select';
  required: boolean;
  default?: string | number;
  description?: string;
  options?: string[];
}

export interface ConfigSchema {
  fields: ConfigField[];
}

export interface Source {
  name: string;
  description: string;
  config_schema: ConfigSchema;
}

export interface ImportStatus {
  id: string;
  source: string;
  phase: 'queued' | 'running' | 'done' | 'error';
  message: string;
  bytes_written: number;
  bytes_expected: number;
  files_written: number;
  files_expected: number;
  started_at: string;
  updated_at: string;
  finished_at?: string;
  error?: string;
}

export interface ObjectInfo {
  key: string;
  size: number;
  content_type?: string;
  last_modified: string;
  etag?: string;
}

export interface ListPage {
  prefix: string;
  delimiter: string;
  prefixes: string[] | null;
  objects: ObjectInfo[] | null;
}

export interface WhoAmI {
  public_url: string;
  bucket: string;
  size_cap: number;
  size_cap_str: string;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  const text = await res.text();
  let parsed: any = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // Non-JSON error body — keep the raw text in the thrown message.
    }
  }
  if (!res.ok) {
    const msg = parsed?.error ?? text ?? res.statusText;
    throw new Error(`${res.status} ${msg}`);
  }
  return parsed as T;
}

export const api = {
  whoami: () => req<WhoAmI>('GET', '/api/whoami'),

  listSources: () => req<Source[]>('GET', '/api/sources'),
  getSource: (name: string) => req<Source>('GET', `/api/sources/${encodeURIComponent(name)}`),

  submitImport: (source: string, config: Record<string, unknown>) =>
    req<ImportStatus>('POST', '/api/imports', { source, config }),
  listImports: () => req<ImportStatus[]>('GET', '/api/imports'),
  getImport: (id: string) => req<ImportStatus>('GET', `/api/imports/${encodeURIComponent(id)}`),
  cancelImport: (id: string) =>
    req<void>('POST', `/api/imports/${encodeURIComponent(id)}/cancel`),

  listGarage: (prefix: string, max = 200) =>
    req<ListPage>(
      'GET',
      `/api/garage/list?prefix=${encodeURIComponent(prefix)}&max=${max}`,
    ),
  statGarage: (key: string) =>
    req<ObjectInfo>('GET', `/api/garage/stat?key=${encodeURIComponent(key)}`),
  // The streaming download route; each key segment is escaped but the
  // slashes stay, since the server matches /api/objects/{key...}.
  objectURL: (key: string) =>
    '/api/objects/' + key.split('/').map(encodeURIComponent).join('/'),
  presignedURL: (key: string, ttl = '15m') =>
    req<{ url: string }>(
      'GET',
      `/api/garage/url?key=${encodeURIComponent(key)}&ttl=${encodeURIComponent(ttl)}`,
    ),
};

export function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}
